import React, { useState, useEffect } from 'react';
import { 
  Box, Button, Typography, Paper, Grid, Alert, Tooltip 
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckIcon from '@mui/icons-material/Check';
import { useCustomTheme, DEFAULT_THEME } from '../../context/ThemeContext';

// Preset di colori pastello adatti allo yoga e al benessere
const PASTEL_PRESETS_LIGHT = [
  { hex: '#F6E6E8', name: 'Rosa Cipria' },
  { hex: '#D4BBDD', name: 'Lilla Salvia' },
  { hex: '#F7E951', name: 'Giallo Sole' },
  { hex: '#E3EEDD', name: 'Verde Salvia' },
  { hex: '#E6F0FA', name: 'Azzurro Polvere' },
  { hex: '#FFF1E6', name: 'Pesca Chiaro' },
  { hex: '#F5F2EB', name: 'Sabbia Natura' },
  { hex: '#FFFFFF', name: 'Bianco' },
];

const PASTEL_PRESETS_DARK = [
  { hex: '#5E376D', name: 'Viola Maha Devi' },
  { hex: '#2E4A3B', name: 'Verde Bosco' },
  { hex: '#6A1B9A', name: 'Prugna' },
  { hex: '#2C3E50', name: 'Blu Notte' },
  { hex: '#8D6E63', name: 'Marrone Caldo' },
  { hex: '#333333', name: 'Antracite' },
];

const COLOR_FIELDS = [
  {
    key: 'logo_title_color',
    label: 'Titolo Principale',
    description: 'Colore del titolo "Spazio Yoga MAHA DEVI"',
    default: DEFAULT_THEME.logo_title_color,
    presets: PASTEL_PRESETS_DARK
  },
  {
    key: 'header_bg',
    label: 'Header (Menu)',
    description: 'Parte alta con la barra di navigazione',
    default: DEFAULT_THEME.header_bg,
    presets: PASTEL_PRESETS_LIGHT
  },
  {
    key: 'body_bg',
    label: 'Body (Sfondo Pagine)',
    description: 'Sfondo generale delle pagine del sito',
    default: DEFAULT_THEME.body_bg,
    presets: PASTEL_PRESETS_LIGHT
  },
  {
    key: 'news_card_bg',
    label: 'Card News ed Eventi',
    description: 'Sfondo dei contenitori news ed eventi',
    default: DEFAULT_THEME.news_card_bg,
    presets: PASTEL_PRESETS_LIGHT
  },
  {
    key: 'schedule_card_bg',
    label: 'Card Orari Corsi',
    description: 'Sfondo dei foglietti degli orari',
    default: DEFAULT_THEME.schedule_card_bg,
    presets: PASTEL_PRESETS_LIGHT
  },
  {
    key: 'footer_bg',
    label: 'Footer (Piè di pagina)',
    description: 'Parte bassa della pagina con i contatti',
    default: DEFAULT_THEME.footer_bg,
    presets: PASTEL_PRESETS_LIGHT
  }
];

export default function ThemeAdmin() {
  const { themeSettings, saveTheme, resetTheme } = useCustomTheme();
  const [colors, setColors] = useState(themeSettings);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    setColors(themeSettings);
  }, [themeSettings]);

  const handleColorChange = (key, value) => {
    setColors(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    const result = await saveTheme(colors);
    setSaving(false);
    if (result.success) {
      setMessage({ type: 'success', text: result.message || 'Colori del tema salvati con successo!' });
    } else {
      setMessage({ type: 'error', text: result.error || 'Errore durante il salvataggio dei colori.' });
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Ripristinare i colori di default del sito? Tutte le personalizzazioni attuali verranno azzerate.")) {
      return;
    }
    setSaving(true);
    setMessage(null);
    const result = await resetTheme();
    setSaving(false);
    setColors(DEFAULT_THEME);
    setMessage({ type: 'info', text: 'Colori di default ripristinati con successo!' });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Colori del sito</Typography>
        <Tooltip title="Azzera tutte le modifiche e torna ai colori originali dello Spazio Yoga">
          <Button 
            variant="outlined" 
            color="warning" 
            startIcon={<RestartAltIcon />} 
            onClick={handleReset}
            disabled={saving}
          >
            Ripristina colori di default
          </Button>
        </Tooltip>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {COLOR_FIELDS.map(field => {
          const currentColor = (colors[field.key] || field.default).toLowerCase();
          const isCustomColor = !field.presets.some(p => p.hex.toLowerCase() === currentColor);

          return (
            <Grid item xs={12} sm={6} key={field.key}>
              <Paper sx={{ p: 2.5, border: '1px solid #e0e0e0', borderRadius: 3, bgcolor: '#ffffff' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {field.label}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {field.description}
                </Typography>

                <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                  Scegli o personalizza il colore:
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                  {/* Primo cerchio: Tavolozza per la scelta personalizzata di qualsiasi colore */}
                  <Tooltip title="Colore su misura: clicca per scegliere un qualsiasi colore dal tavolozza">
                    <Box 
                      sx={{ 
                        position: 'relative', 
                        width: 38, 
                        height: 38, 
                        cursor: 'pointer',
                        transition: 'transform 0.15s ease-in-out',
                        '&:hover': { transform: 'scale(1.15)' }
                      }}
                    >
                      <input 
                        type="color" 
                        value={colors[field.key] || field.default} 
                        onChange={(e) => handleColorChange(field.key, e.target.value)}
                        style={{
                          position: 'absolute',
                          top: 0, left: 0, width: '100%', height: '100%',
                          opacity: 0, cursor: 'pointer', zIndex: 2
                        }}
                      />
                      <Box 
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: '50%',
                          backgroundColor: colors[field.key] || field.default,
                          border: isCustomColor ? '3px solid #1976d2' : '2px dashed #999',
                          boxShadow: isCustomColor ? '0 0 8px rgba(25, 118, 210, 0.5)' : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '18px'
                        }}
                      >
                        🎨
                      </Box>
                    </Box>
                  </Tooltip>

                  {/* Preset consigliati */}
                  {field.presets.map((preset) => {
                    const isSelected = currentColor === preset.hex.toLowerCase();
                    return (
                      <Tooltip key={preset.hex} title={preset.name}>
                        <Box
                          onClick={() => handleColorChange(field.key, preset.hex)}
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '50%',
                            backgroundColor: preset.hex,
                            border: isSelected ? '3px solid #1976d2' : '1px solid #ccc',
                            boxShadow: isSelected ? '0 0 8px rgba(25, 118, 210, 0.5)' : 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'transform 0.15s ease-in-out',
                            '&:hover': {
                              transform: 'scale(1.15)',
                            }
                          }}
                        >
                          {isSelected && (
                            <CheckIcon 
                              sx={{ 
                                fontSize: 18, 
                                color: ['#ffffff', '#f5f2eb', '#e6f0fa', '#e3eedd', '#fff1e6', '#f6e6e8', '#f7e951'].includes(preset.hex.toLowerCase()) ? '#333' : '#fff' 
                              }} 
                            />
                          )}
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button 
          variant="contained" 
          color="primary" 
          size="large"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ py: 1.5, px: 4, borderRadius: 2 }}
        >
          {saving ? 'Salvataggio...' : 'Salva Modifiche Tema'}
        </Button>
      </Box>
    </Box>
  );
}
